<script setup lang="ts">
import type { PropType } from 'vue'
import type { Category, Tag } from '@/models/content'

const props = defineProps({
  id: {
    type: String,
    require: true,
  },
  title: {
    type: String,
    required: true,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  summary: {
    type: String,
    required: true,
  },
  issue: {
    type: String,
    required: true,
  },
  quarter: {
    type: String,
    required: true,
  },
  pills: {
    type: Array as PropType<Tag[] | Category[]>,
    required: true,
  },
  pillsLimit: {
    type: Number,
    default: 2,
  },
  coverUrl: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    required: true,
  },
  keepMobileLayout: {
    type: Boolean,
    default: false,
  },
})

const limitedPills = computed(() => props.pills.slice(0, props.pillsLimit))
const coverPosition = useCoverPosition(props.coverUrl)

const { callToAction, isDownloading, downloadProgress } = useDownloadFile()
</script>

<template>
  <ArchiveCard
    :date-and-duration="publishedDate"
    :title="`${quarter} | ${title} - ${issue}`"
    :pills="limitedPills"
    @click="callToAction.callback(id, 'magazine', fileName)"
  >
    <template #cover>
      <div
        class="flex h-full w-full justify-center md:justify-start md:gap-x-12"
      >
        <div class="relative w-full overflow-hidden md:h-52 md:w-36">
          <DownloadOverlay
            :is-downloading="isDownloading"
            :download-progress="downloadProgress"
            :label="callToAction.text"
          />

          <img
            :src="$cloudinary.resize(coverUrl, 'magazine-thumbnail')"
            class="h-full w-full object-cover"
            :class="coverPosition"
            alt=""
            width="300"
            height="500"
          />

          <div class="absolute top-0 h-full w-full border-2 border-black/10" />
        </div>

        <div class="hidden shrink-0 md:block">
          <p class="text-lg font-semibold leading-snug md:text-xl md:font-bold">
            {{ quarter }}
          </p>
          <p class="text-sm">{{ issue }}</p>
        </div>
      </div>
    </template>

    <template #details>
      <div class="custom-line-clamp" v-html="summary" />

      <!-- Date (Destkop) -->
      <p class="text-sm font-semibold text-s-700">
        {{ publishedDate }}
      </p>
    </template>
  </ArchiveCard>
</template>
